import { FC, useEffect, useState } from 'react';
// import { Link } from 'react-router-dom';
import cn from 'classnames';

import { Icon, Text, TextButton } from 'shared/components/ui';
import { CandidateProfileSkeleton } from 'features/candidate/components';
// import { SearchCompany, CLevelStaff } from 'shared/generated-models';
import { CompanySkillMatch, ProgAIResultsCompany } from 'shared/generated-models';
import { ProfileVariant } from 'features/candidate';
import { ReadMoreText } from 'shared/components';
import { CandidateProfileAvatar } from 'shared/components';

import { CompanyInfo } from '../CompanyInfo';

import styles from './index.module.scss';

interface Props {
	data?: ProgAIResultsCompany;
	shared?: boolean;
	className?: string;
	variant: ProfileVariant;
}

// interface LeaderProfileProps {
// 	leader: CLevelStaff;
// }

// const LeaderProfile = ({ leader }: LeaderProfileProps) => {
// 	const handleClick = () => {
// 		window.open(leader.linkedin_url, '_blank');
// 	};

// 	return (
// 		<div className={styles.leader}>
// 			<Icon icon="person-bold" size={20} className={styles.leaderIcon} />
// 			<div className={styles.leaderColumn}>
// 				<div className={styles.leaderRow}>
// 					<Link
// 						to={`/search/results?page=0&linkedin_url=${leader.linkedin_url}&size=100&detailed=True`}
// 						target="_blank"
// 						rel="noopener,nofollow,noreferrer"
// 						className={styles.buttonEmployees}>
// 						<p className={styles.leaderName}>{`${leader.fist_name} ${leader.last_name}`}</p>
// 					</Link>
// 					<Icon icon="linkedin-logo" size={16} onClick={handleClick} className={styles.leaderLinkedin} />
// 				</div>
// 				<Text variant="inter/14/regular" color="grey">
// 					{leader.title}
// 				</Text>
// 			</div>
// 		</div>
// 	);
// };

const formatLocation = (city?: string, country?: string) => {
	if (!city && !country) return '';
	if (!country) return city;
	if (!city) return country;
	return `${city}, ${country}`;
};

const companyInitials = (name?: string): string => {
	if (!name) return '';
	const words = name.trim().split(/\s+/);
	const initials = words.map(word => word.charAt(0).toUpperCase()).join('');
	return initials;
};

const extractDomain = (url?: string): string => {
	if (!url) return '';
	try {
		const { protocol, host } = new URL(url);
		return `${protocol}//${host}`;
	} catch (error) {
		console.error('Invalid URL:', url);
		return '';
	}
};

const Tag = ({ tag }: { tag: CompanySkillMatch }) => {
	return (
		<div className={styles.tag}>
			<Text variant="inter/12/semi">{tag.weight}</Text>
			<Text variant="inter/14/regular" className={styles.tagDivider}>
				|
			</Text>
			<Text variant="inter/12/semi" color="dark_green">
				{tag.skill}
			</Text>
		</div>
	);
};

const MAX_SKILLS = 9;

export const CompanyProfile: FC<Props> = ({ data, shared, variant, className }: Props) => {
	const [logo, setLogo] = useState<string | null>(null);
	const [showMore, setShowMore] = useState<boolean>(false);

	const company = data?.profile;

	useEffect(() => {
		const fetchLogo = async () => {
			try {
				const logoUrl = `https://logo.clearbit.com/${extractDomain(company?.website)}`;
				const response = await fetch(logoUrl);
				if (response.ok) {
					setLogo(logoUrl);
				} else {
					setLogo(null);
				}
			} catch (error) {
				console.error('Failed to fetch logo:', error);
				setLogo(null);
			}
		};

		fetchLogo();
	}, [company]);

	if (!company) return <CandidateProfileSkeleton />;

	return (
		<section className={cn(styles.profile, className)}>
			<div className={styles.wrapper}>
				<section className={styles.info}>
					<div className={styles.infoRow}>
						{logo ? (
							<img className={styles.logo} src={logo} alt="logo" />
						) : (
							<CandidateProfileAvatar size={60}>{companyInitials(company.domain)}</CandidateProfileAvatar>
						)}
						<div className={styles.infoColumn}>
							<Text variant="inter/20/bold" className={className}>
								{company.company_name}
							</Text>
							<div className={styles.location}>
								<Icon icon="location" className={styles.locationIcon} />
								<Text color="dark_green" variant="inter/14/medium">
									{formatLocation(company.city, company.country)}
								</Text>
							</div>
						</div>
					</div>
					<div className={styles.icons}>
						{company.website && (
							<a className={styles.iconsWebsite} href={company.website} target="_blank" rel="noreferrer">
								<Icon icon="website" size={20} />
							</a>
						)}
						{company.cleanCompanyLinkedinUrl && (
							<a
								className={styles.iconsLinkedin}
								href={`https://linkedin.com/company/${company.cleanCompanyLinkedinUrl}`}
								target="_blank"
								rel="noreferrer">
								<Icon icon="linkedin-logo" size={20} />
							</a>
						)}
					</div>
				</section>
				<div className={styles.sections}>
					<div className={styles.sectionsWrapper}>
						<Text component="h3" variant="inter/18/semi" className={styles.title}>
							Summary
						</Text>
						{company.company_description && (
							<div className={styles.description}>
								<ReadMoreText text={company.company_description} />
							</div>
						)}
						<CompanyInfo company={company} />
						<div className={styles.skillsWrapper}>
							<div className={styles.skillsHeader}>
								<div className={styles.skillsTitle}>
									<Icon icon="users" size={16} className={styles.skillsIcon} />
									<Text variant="inter/15/semi" color="dark_blue">
										Skill Distribution
									</Text>
								</div>
								<TextButton
									onClick={() => {
										setShowMore(!showMore);
									}}>
									{showMore ? 'See less' : 'More'}
								</TextButton>
							</div>
							<Text variant="inter/14/medium" className={styles.skillsDescription}>
								Number of developers with skills
							</Text>
							<div className={styles.skills}>
								{(showMore
									? data.match_metadata.found_skills
									: data.match_metadata.found_skills?.slice(0, MAX_SKILLS)
								)?.map(tag => (
									<Tag tag={tag} />
								))}
							</div>
						</div>
					</div>
				</div>
				{/* {company.c_level_staff && (
					<div className={styles.sections}>
						<Text component="h3" variant="inter/18/semi" className={styles.title}>
							C-Level/Leadership
						</Text>
						<div className={styles.column}>
							{company.c_level_staff.map(leader => (
								<LeaderProfile leader={leader} />
							))}
						</div>
					</div>
				)} */}
			</div>
		</section>
	);
};
